<template>
    <FormSection
        class="o-gdprPolicies"
        data-qa="gdpr policies"
    >
        <FormElement>
            <FormCheckbox
                v-model="generalPolicy"
                :error="v$.generalPolicy.$error"
                data-qa="general policy"
                @blur="v$.generalPolicy.$touch()"
                @update:model-value="track(
                    {
                        element: 'AP_F: GDPR',
                        elementDetail: 'GDPR-Checkbox karriere.at',
                    })"
            >
                <AppLink
                    :tag="gdprTexts.generalPolicyUrl ? undefined : 'button'"
                    data-qa="general policy link"
                    :href="gdprTexts.generalPolicyUrl"
                    :class="{ 'o-gdprPolicies__link--hasIcon': gdprTexts.generalPolicyUrl }"
                    class="link"
                    target="_blank"
                    rel="noopener noreferrer"
                    @click="openModal(GDPR_TYPE.general, gdprTexts.generalPolicyUrl, $event)"
                >
                    karriere.at Datenschutzerklärung *
                    <AppIcon
                        v-if="gdprTexts.generalPolicyUrl"
                        class="o-gdprPolicies__externalLinkIcon"
                        name="external-link"
                        size="l"
                    />
                </AppLink>
                <p
                    data-qa="privacy policy text"
                    v-html="privacyPolicyText"
                />
            </FormCheckbox>
            <template #end>
                <FormMessage
                    v-if="v$.generalPolicy.$error"
                    type="error"
                    data-qa="general policy error"
                >
                    Bitte akzeptiere die Datenschutzerklärung
                </FormMessage>
            </template>
        </FormElement>
        <FormElement
            v-if="gdprTexts.companyPolicy"
            class="o-gdprPolicies__companyPolicy"
        >
            <FormCheckbox
                v-model="companyPolicy"
                :error="v$.companyPolicy.$error"
                data-qa="company policy"
                @blur="v$.companyPolicy.$touch()"
            >
                <AppLink
                    :tag="gdprTexts.companyPolicyUrl ? undefined : 'button'"
                    :href="gdprTexts.companyPolicyUrl"
                    data-qa="company policy link"
                    class="text-left link"
                    :class="{ 'o-gdprPolicies__link--hasIcon': gdprTexts.companyPolicyUrl }"
                    target="_blank"
                    rel="noopener noreferrer"
                    @click="openModal(GDPR_TYPE.company, gdprTexts.companyPolicyUrl, $event)"
                >
                    {{ companyName }} Datenschutzerklärung *
                    <AppIcon
                        v-if="gdprTexts.companyPolicyUrl"
                        class="o-gdprPolicies__externalLinkIcon"
                        name="external-link"
                        size="l"
                    />
                </AppLink>
            </FormCheckbox>
            <template #end>
                <FormMessage
                    v-if="v$.companyPolicy.$error"
                    type="error"
                >
                    Bitte akzeptiere die Datenschutzerklärung
                </FormMessage>
            </template>
        </FormElement>
        <FormElement>
            <FormCheckbox
                v-if="gdprTexts.companyConsent"
                v-model="companyConsent"
                :error="v$.companyConsent.$error"
                data-qa="company consent"
                @blur="v$.companyConsent.$touch()"
                @update:model-value="track(
                    {
                        element: 'AP_F: GDPR',
                        elementDetail: 'GDPR-Checkbox Company',
                    })"
            >
                <AppLink
                    :tag="gdprTexts.companyConsentUrl ? undefined : 'button'"
                    data-qa="company consent link"
                    :href="gdprTexts.companyConsentUrl"
                    :class="{ 'o-gdprPolicies__link--hasIcon': gdprTexts.companyConsentUrl }"
                    class="link"
                    target="_blank"
                    rel="noopener noreferrer"
                    @click="openModal(GDPR_TYPE.consent, gdprTexts.companyConsentUrl, $event)"
                >
                    {{ companyName }} Evidenzerklärung {{ gdprTexts.companyConsentMandatory ? '*' : '' }}
                    <AppIcon
                        v-if="gdprTexts.companyConsentUrl"
                        class="o-gdprPolicies__externalLinkIcon"
                        name="external-link"
                        size="l"
                    />
                </AppLink>
                <p
                    data-qa="company policy text"
                    v-html="companyPolicyText"
                />
            </FormCheckbox>
            <template #end>
                <FormMessage
                    v-if="v$.companyConsent.$error"
                    type="error"
                >
                    Bitte akzeptiere die Evidenzerklärung
                </FormMessage>
            </template>
        </FormElement>
        <ModalGdpr
            v-model:open="modals.gdpr"
            data-qa="gdpr policy modal"
        >
            <template #headline>
                <template v-if="activeGdprType === GDPR_TYPE.general">
                    Wir von karriere.at GmbH nehmen deinen Datenschutz ernst!
                </template>
                <template v-else-if="activeGdprType === GDPR_TYPE.company">
                    Datenschutzvereinbarung von <span v-html="companyName"/>
                </template>
                <template v-else-if="activeGdprType === GDPR_TYPE.consent">
                    Evidenzerklärung
                </template>
            </template>
            <template
                v-if="activeGdprType === GDPR_TYPE.general || GDPR_TYPE.company"
                #subHeadline
            >
                Bitte lies dir folgende Informationen aufmerksam durch:
            </template>
            <span
                v-if="activeGdprType === GDPR_TYPE.general"
                v-html="gdprTexts.generalPolicyBody"
            />
            <span
                v-else-if="activeGdprType === GDPR_TYPE.company"
                v-html="gdprTexts.companyPolicyBody"
            />
            <span
                v-else-if="activeGdprType === GDPR_TYPE.consent"
                v-html="gdprTexts.companyConsentBody"
            />
        </ModalGdpr>
    </FormSection>
</template>

<script setup>
import {
    computed, ref,
} from 'vue';
import useVuelidate from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import {
    track,
} from '../../utils/tracking';

import AppLink from '../atoms/app/AppLink.vue';
import AppIcon from '../atoms/app/AppIcon.vue';
import FormCheckbox from '../atoms/form/FormCheckbox.vue';
import FormElement from '../molecules/form/FormElement.vue';
import FormMessage from '../atoms/form/FormMessage.vue';
import FormSection from './form/FormSection.vue';
import ModalGdpr from './modal/ModalGdpr.vue';
import { useModalStore } from '../../stores/modal-store';
import { useGdprTextsStore } from '../../stores/gdpr-texts-store';
import { usePayloadStore } from '../../stores/payload-store';
import { useProfile } from '../../stores/profile-store';
import { useJobDataStore } from '../../stores/job-store';

const GDPR_TYPE = {
    general: 'GENERAL',
    company: 'COMPANY',
    consent: 'CONSENT',
};

const activeGdprType = ref(null);

const modals = useModalStore();
const payload = usePayloadStore();
const gdprTexts = useGdprTextsStore();
const profile = useProfile();
const jobData = useJobDataStore();

const companyName = computed(() => payload.companyName);
const isKarriereAtApplication = computed(() => payload.companyName === 'karriere.at');
const privacyPolicyText = computed(() => {
    const introduction = 'Wir behandeln deine Daten streng vertraulich.';
    const companyDisplayName = jobData.isChiffre ? 'das Unternehmen' : `<strong>${payload.companyName}</strong>`;
    const usageType = isKarriereAtApplication.value ?
        'karriere.at erhält deine Bewerbung direkt' :
        `karriere.at übermittelt deine Bewerbung direkt an ${companyDisplayName}`;
    const additionalInfo = profile.hasUser ? ' und speichert sie in deinem persönlichen Profil' : '';
    return `${introduction} ${usageType}${additionalInfo}.`;
});
const companyPolicyText =
    'Erhältst du für diesen Job keine Zusage, ' +
    'kann das Unternehmen deine Daten speichern und dich kontaktieren, ' +
    'sobald eine passendere Stelle frei wird.';

const {
    generalPolicyChecked: generalPolicy,
    companyPolicyChecked: companyPolicy,
    companyConsentChecked: companyConsent,
} = storeToRefs(gdprTexts);

function openModal(gdprType, gdprUrl, event) {
    if (gdprUrl) return;
    if (event) event.preventDefault();
    activeGdprType.value = gdprType;
    modals.gdpr = true;
}

const validations = {
    generalPolicy: {
        checked: isChecked => Boolean(isChecked),
    },
    companyPolicy: {
        checked: isChecked => Boolean(isChecked) || gdprTexts.companyPolicy === null,
    },
    companyConsent: {
        checked: isChecked => isChecked || !gdprTexts.companyConsentMandatory,
    },
};
const v$ = useVuelidate(validations, { generalPolicy, companyPolicy, companyConsent }, { $registerAs: 'gdprPolicies' });

</script>

<style lang="scss">
@import '~@karriereat/global-styles/scss/settings/spacing';
@import '~@karriereat/global-styles/scss/settings/breakpoint';
@import '~@karriereat/global-styles/scss/settings/font-size';

.o-gdprPolicies {
    $external-link-icon-visual-correction: 1px;

    display: flex;
    flex-direction: column;
    gap: $k-spacing--l;

    &__link {
        &--hasIcon {
            display: flex;
            align-items: center;
        }
    }

    &__externalLinkIcon {
        margin-top: $external-link-icon-visual-correction;
        margin-left: $k-spacing--s;
    }
}
</style>
